import type {
  EffectiveDomain,
  EffectiveRole,
  EffectiveRoleSource,
} from '../types/effectiveRoles.types'

export type HeldRole = EffectiveRole & {
  // The roles of its own domain it implies, nearest first, each with only the
  // reasons it is held for besides those roles
  includes: EffectiveRole[]
}

// A domain's held roles as a person reads them: every role that another held
// role of the same domain implies is listed under that role ("Manager, includes
// Reader") instead of on a line of its own, even when it is also held for a
// reason of its own, which it keeps.
export const foldImpliedRoles = (domain: EffectiveDomain): HeldRole[] => {
  const held = new Set(domain.roles.map(({ id }) => id))
  const isImpliedByHeldRole = (
    source: EffectiveRoleSource
  ): source is Extract<EffectiveRoleSource, { type: 'role' }> =>
    source.type === 'role' &&
    source.domain.id === domain.id &&
    held.has(source.role.id)

  const implies = new Map<string, EffectiveRole[]>()
  domain.roles.forEach((role) =>
    role.sources.forEach((source) => {
      if (isImpliedByHeldRole(source)) {
        implies.set(source.role.id, [
          ...(implies.get(source.role.id) ?? []),
          role,
        ])
      }
    })
  )

  const includesOf = (top: EffectiveRole) => {
    const reached = [top]
    // Breadth first, so the roles it implies directly come first
    for (const role of reached) {
      for (const implied of implies.get(role.id) ?? []) {
        if (!reached.includes(implied)) reached.push(implied)
      }
    }

    return reached.slice(1).map((role) => ({
      ...role,
      sources: role.sources.filter((source) => !isImpliedByHeldRole(source)),
    }))
  }

  return domain.roles
    .filter((role) => !role.sources.some(isImpliedByHeldRole))
    .map((role) => ({ ...role, includes: includesOf(role) }))
}
